// A social post in three clicks: say what it's about → Write it → Post now.
// The owner's click is the approval. Below: recent posts with each network's
// outcome, page by page.
import {
  type SocialNetwork,
  type SocialPost,
  cancelSocialPost,
  createSocialPost,
  fetchSocialNetworks,
  fetchSocialPosts,
  retrySocialPost,
  socialImage,
  writeSocialPost
} from '@/lib/ai-social'
import { notifyError, queryClient } from '@/lib/query'
import { cn } from '@/lib/utils'
import { CHIP, card } from '@/pages/ai-team/hubs'
import { timeAgo } from '@/pages/ai-team/workflows/shared'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { useQuery } from '@tanstack/react-query'
import {
  Check,
  ChevronDown,
  ExternalLink,
  ImagePlus,
  Loader2,
  RefreshCw,
  Send,
  Sparkles,
  Upload,
  X
} from 'lucide-react'
import { type ChangeEvent, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Pager, field, ghostBtn, goBtn, sectionTitle } from './parts'

const SUBREDDIT_KEY = 'ai-social-subreddit'

/** Characters as the networks count them (an emoji is one, not two UTF-16 units). */
const charCount = (s: string) => [...s].length

function remembered() {
  try {
    return localStorage.getItem(SUBREDDIT_KEY) || ''
  } catch {
    return ''
  }
}

/** `at` (a datetime-local value) opens it scheduled for that time, as the calendar does; `compact` drops the outer card. */
export function NewPostCard({ at: plannedAt, compact = false, onDone }: { at?: string; compact?: boolean; onDone?: () => void } = {}) {
  const nets = useQuery({ queryKey: ['ai-social-networks'], queryFn: fetchSocialNetworks })
  const [topic, setTopic] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [text, setText] = useState('')
  const [title, setTitle] = useState('')
  const [subreddit, setSubreddit] = useState(remembered)
  const [image, setImage] = useState('')
  const [imagePrompt, setImagePrompt] = useState('')
  const [instruction, setInstruction] = useState('')
  const [kind, setKind] = useState('')
  const [when, setWhen] = useState<'now' | 'later'>(plannedAt ? 'later' : 'now')
  const [at, setAt] = useState(plannedAt ?? '')
  const [busy, setBusy] = useState<'write' | 'image' | 'upload' | 'post' | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  const networks = nets.data?.networks ?? []
  const chosen = networks.filter((n) => picked.includes(n.key))
  const needsImage = chosen.some((n) => n.needs_image)
  const needsTitle = chosen.some((n) => n.needs_title)
  const limit = Math.min(...chosen.map((n) => n.limit), Number.POSITIVE_INFINITY)
  const length = charCount(text)
  const tooLong = Number.isFinite(limit) && length > limit

  // A file dropped just outside the drop zone would otherwise open in the tab
  // and lose the post. Only file drags are stopped; text drags work as usual.
  useEffect(() => {
    const stop = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes('Files')) e.preventDefault()
    }
    document.addEventListener('dragover', stop)
    document.addEventListener('drop', stop)
    return () => {
      document.removeEventListener('dragover', stop)
      document.removeEventListener('drop', stop)
    }
  }, [])

  function toggle(n: SocialNetwork) {
    if (!n.ready) return
    setPicked((p) => (p.includes(n.key) ? p.filter((k) => k !== n.key) : [...p, n.key]))
  }

  async function write(rewrite = false) {
    if (!topic.trim() && !text.trim()) return toast.error('Say what it’s about, or write a few words first.')
    setBusy('write')
    try {
      const out = await writeSocialPost({
        topic: topic.trim(),
        networks: picked,
        kind,
        instruction: rewrite ? instruction.trim() : '',
        current: rewrite ? text : ''
      })
      setText(out.text)
      if (!title || !rewrite) setTitle(out.title)
      setInstruction('')
    } catch (err) {
      notifyError("Couldn't write it", err)
    } finally {
      setBusy(null)
    }
  }

  async function makeImage() {
    const prompt = imagePrompt.trim() || topic.trim()
    if (!prompt) return toast.error('Describe the image, or upload one.')
    setBusy('image')
    try {
      setImage((await socialImage({ prompt })).url)
    } catch (err) {
      notifyError("Couldn't make the image", err)
    } finally {
      setBusy(null)
    }
  }

  async function upload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (file) await uploadFile(file)
  }

  async function uploadFile(file: File) {
    if (busy !== null) return
    if (!file.type.startsWith('image/')) return toast.error('Use a JPG, PNG, WebP or GIF image.')
    if (file.size > 8 * 1024 * 1024) return toast.error('That image is over 8 MB.')
    setBusy('upload')
    try {
      const dataUri = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result))
        reader.onerror = () => reject(new Error('Could not read the file.'))
        reader.readAsDataURL(file)
      })
      setImage((await socialImage({ data_uri: dataUri })).url)
    } catch (err) {
      notifyError("Couldn't upload the image", err)
    } finally {
      setBusy(null)
    }
  }

  async function post() {
    setBusy('post')
    try {
      if (needsTitle) {
        try {
          localStorage.setItem(SUBREDDIT_KEY, subreddit)
        } catch {
          // fine: it just won't be remembered
        }
      }
      await createSocialPost({
        text: text.trim(),
        title: title.trim(),
        image_url: image,
        networks: picked,
        subreddit: subreddit.trim(),
        at: when === 'later' && at ? new Date(at).toISOString() : undefined
      })
      toast.success(when === 'later' ? 'Scheduled. It goes out by itself.' : 'Posting now…')
      setTopic('')
      setText('')
      setTitle('')
      setImage('')
      setImagePrompt('')
      setAt(plannedAt ?? '')
      setWhen(plannedAt ? 'later' : 'now')
      onDone?.()
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ai-social-posts'] }),
        queryClient.invalidateQueries({ queryKey: ['ai-calendar'] })
      ])
    } catch (err) {
      notifyError("Couldn't post it", err)
    } finally {
      setBusy(null)
    }
  }

  const canPost =
    !!text.trim() &&
    picked.length > 0 &&
    !tooLong &&
    (!needsTitle || !!subreddit.trim()) &&
    (when === 'now' || !!at)

  return (
    <section id={compact ? undefined : 'new-post'} className={cn(!compact && card, 'space-y-5')}>
      {!compact && (
        <div>
          <h2 className={sectionTitle}>New post</h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Write it yourself or let the AI help. Your click is the approval.
          </p>
        </div>
      )}

      {/* Where + type */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">Where</span>
          {nets.isLoading ? (
            <div className="h-11 w-full animate-pulse rounded-full bg-neutral-200 dark:bg-neutral-800" />
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className={cn(field, 'flex items-center justify-between gap-2 text-left')}>
                  <span className={cn('truncate', !chosen.length && 'text-neutral-400 dark:text-neutral-500')}>
                    {chosen.length ? chosen.map((n) => n.label).join(', ') : 'Choose platforms'}
                  </span>
                  <ChevronDown className="size-4 shrink-0 text-neutral-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 rounded-[24px] p-1.5">
                {networks.map((n) => (
                  <DropdownMenuCheckboxItem
                    key={n.key}
                    checked={picked.includes(n.key)}
                    disabled={!n.ready}
                    onCheckedChange={() => toggle(n)}
                    onSelect={(e) => e.preventDefault()}
                    className="rounded-full"
                  >
                    <span className="flex-1">{n.label}</span>
                    {!n.ready && <span className="text-xs text-neutral-400">not connected</span>}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <label className="block space-y-1">
          <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">Type of post</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)} className={field}>
            <option value="">Any</option>
            {(nets.data?.types ?? []).map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {nets.isError && (
        <p role="alert" className="-mt-3 flex flex-wrap items-center gap-2 text-xs font-semibold text-red-600 dark:text-red-400">
          Couldn't load the platforms.
          <button type="button" onClick={() => nets.refetch()} className="rounded-full underline underline-offset-2">
            Try again
          </button>
        </p>
      )}
      {networks.some((n) => !n.ready) && !nets.isLoading && (
        <p className="-mt-3 text-xs text-neutral-500 dark:text-neutral-400">
          Missing a platform?{' '}
          <Link to="/apps/ai-team/connections" className="font-semibold text-neutral-900 underline dark:text-white">
            Connect it
          </Link>
        </p>
      )}
      {needsTitle && (
        <label className="block space-y-1">
          <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">Subreddit</span>
          <input value={subreddit} onChange={(e) => setSubreddit(e.target.value)} placeholder="r/…" className={field} />
        </label>
      )}

      {/* Image: your own, or one made for you */}
      <div className="space-y-2">
        <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">Image</span>
        <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={upload} className="hidden" />
        {image ? (
          <div className="relative w-fit">
            <img src={image} alt="The post's image" className="h-44 w-44 rounded-[24px] object-cover" />
            <button
              type="button"
              onClick={() => setImage('')}
              aria-label="Remove image"
              className="absolute right-2 top-2 inline-flex size-8 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
            >
              <X className="size-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault()
              if (busy !== null) return
              const file = e.dataTransfer.files?.[0]
              if (file) void uploadFile(file)
            }}
            disabled={busy !== null}
            className="flex w-full flex-col items-center justify-center gap-2 rounded-[24px] border-2 border-dashed border-neutral-300 px-6 py-8 text-center transition-colors hover:border-emerald-500 hover:bg-emerald-500/5 disabled:opacity-50 dark:border-neutral-700"
          >
            {busy === 'upload' ? (
              <Loader2 className="size-6 animate-spin text-emerald-500" />
            ) : (
              <Upload className="size-6 text-neutral-400" />
            )}
            <span className="text-sm font-semibold text-neutral-900 dark:text-white">
              {busy === 'upload' ? 'Uploading…' : 'Upload an image'}
            </span>
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              Click or drag a JPG, PNG, WebP or GIF here (up to 8 MB)
              {needsImage ? '. No image? Instagram gets one made for you.' : ''}
            </span>
          </button>
        )}
        {!image && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-neutral-500 dark:text-neutral-400">or</span>
            <input
              value={imagePrompt}
              onChange={(e) => setImagePrompt(e.target.value)}
              placeholder="describe one and let AI make it"
              aria-label="Describe an image for AI to make"
              className={cn(field, 'min-w-0 flex-1 py-2')}
            />
            <button type="button" onClick={makeImage} disabled={busy !== null} className={cn(ghostBtn, 'py-2')}>
              {busy === 'image' ? <Loader2 className="size-4 animate-spin" /> : <ImagePlus className="size-4" />}
              Make with AI
            </button>
          </div>
        )}
      </div>

      {/* The post: type it, or let AI write it */}
      <div className="space-y-2">
        <label className="block space-y-1">
          <span className="flex justify-between text-xs font-semibold text-neutral-500 dark:text-neutral-400">
            <span>The post</span>
            {Number.isFinite(limit) && text && (
              <span className={cn('tabular-nums', tooLong && 'text-red-600 dark:text-red-400')}>
                {length} / {limit}
              </span>
            )}
          </span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            disabled={busy === 'write'}
            placeholder="Write your post here…"
            className={cn(field, 'resize-y rounded-[24px] leading-6')}
          />
        </label>
        <div className="flex flex-wrap items-center gap-2 rounded-[24px] bg-emerald-500/5 p-2">
          <Sparkles className="ml-2 size-4 shrink-0 text-emerald-500" />
          <label className="sr-only" htmlFor="post-topic">
            What should the AI write about
          </label>
          <input
            id="post-topic"
            value={text ? instruction : topic}
            onChange={(e) => (text ? setInstruction(e.target.value) : setTopic(e.target.value))}
            placeholder={text ? 'Ask AI to change it: “shorter”, “more fun”…' : 'Or tell AI what it’s about and it writes it'}
            className="min-w-0 flex-1 bg-transparent px-2 py-2 text-sm text-neutral-900 outline-none placeholder:text-neutral-500 dark:text-white"
          />
          <button
            type="button"
            onClick={() => write(!!text)}
            disabled={busy !== null || (text ? false : !topic.trim())}
            className={cn(ghostBtn, 'py-2')}
          >
            {busy === 'write' ? <Loader2 className="size-4 animate-spin" /> : text ? <RefreshCw className="size-4" /> : <Sparkles className="size-4" />}
            {text ? 'Rewrite with AI' : 'Write it for me'}
          </button>
        </div>
      </div>

      {/* When + post */}
      <div className="flex flex-wrap items-center gap-3 border-t border-neutral-100 pt-4 dark:border-neutral-800">
        <div className="flex gap-1 rounded-full bg-neutral-100 p-1 dark:bg-neutral-900">
          {(['now', 'later'] as const).map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setWhen(w)}
              aria-pressed={when === w}
              className={cn(
                'rounded-full px-4 py-1.5 text-sm font-semibold transition-colors',
                when === w ? 'bg-white text-neutral-900 shadow-sm dark:bg-neutral-800 dark:text-white' : 'text-neutral-500 dark:text-neutral-400'
              )}
            >
              {w === 'now' ? 'Now' : 'Pick a time'}
            </button>
          ))}
        </div>
        {when === 'later' && (
          <input
            type="datetime-local"
            value={at}
            onChange={(e) => setAt(e.target.value)}
            aria-label="When to post"
            className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm text-neutral-900 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white"
          />
        )}
        <button type="button" onClick={post} disabled={busy !== null || !canPost} className={cn(goBtn, 'ml-auto')}>
          {busy === 'post' ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
          {when === 'now' ? 'Post now' : 'Schedule'}
        </button>
      </div>
      {!canPost && (
        <p className="-mt-3 text-right text-xs text-neutral-500 dark:text-neutral-400">
          {picked.length === 0
            ? 'Choose where to post it.'
            : !text.trim()
              ? 'Write the post, or let AI write it.'
              : tooLong
                ? 'Too long for one of the platforms.'
                : needsTitle && !subreddit.trim()
                  ? 'Which subreddit?'
                  : when === 'later' && !at
                    ? 'Pick a time.'
                    : ''}
        </p>
      )}
    </section>
  )
}

const STATUS_TONE: Record<SocialPost['status'], keyof typeof CHIP> = {
  scheduled: 'sky',
  posting: 'emerald',
  posted: 'emerald',
  partly: 'amber',
  failed: 'red',
  cancelled: 'neutral'
}

/** Something is about to change: posting now, or due within ten minutes. */
const SOON_MS = 10 * 60_000
const busyPost = (p: SocialPost) =>
  p.status === 'posting' ||
  (p.status === 'scheduled' && !!p.scheduled_for && new Date(p.scheduled_for).getTime() - Date.now() < SOON_MS)

export function RecentPosts() {
  const [page, setPage] = useState(1)
  const posts = useQuery({
    queryKey: ['ai-social-posts', page],
    queryFn: () => fetchSocialPosts(page),
    refetchInterval: (q) => (q.state.data?.posts.some(busyPost) ? 5_000 : 30_000),
    // Keep the list (and the pager) on screen while the next page loads.
    placeholderData: (prev) => prev
  })
  const data = posts.data
  // Posts were removed and this page no longer exists: go back to the last one.
  const lastPage = data?.pages
  useEffect(() => {
    if (lastPage !== undefined && page > Math.max(1, lastPage)) setPage(Math.max(1, lastPage))
  }, [lastPage, page])

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className={sectionTitle}>Recent posts</h2>
        {data && data.pages > 1 && <Pager page={Math.min(page, data.pages)} pages={data.pages} onPage={setPage} />}
      </div>
      {posts.isLoading ? (
        <div className="h-32 animate-pulse rounded-[36px] bg-neutral-200 motion-reduce:animate-none dark:bg-neutral-800" />
      ) : posts.isError && !data ? (
        <div className={cn(card, 'flex flex-wrap items-center gap-2')}>
          <p className="text-sm text-red-600 dark:text-red-400">Couldn't load your posts.</p>
          <button type="button" onClick={() => posts.refetch()} className={cn(ghostBtn, 'px-4 py-1.5')}>
            Try again
          </button>
        </div>
      ) : !data || data.posts.length === 0 ? (
        <div className={card}>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">No posts yet. Your first one shows up here.</p>
        </div>
      ) : (
        <ul className={cn(card, 'divide-y divide-neutral-100 p-2 dark:divide-neutral-800')}>
          {data.posts.map((p) => (
            <PostRow key={p.id} post={p} />
          ))}
        </ul>
      )}
    </section>
  )
}

function PostRow({ post: p }: { post: SocialPost }) {
  const [busy, setBusy] = useState(false)
  async function run(fn: () => Promise<unknown>, done: string) {
    if (busy) return
    setBusy(true)
    try {
      await fn()
      toast.success(done)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ai-social-posts'] }),
        queryClient.invalidateQueries({ queryKey: ['ai-calendar'] })
      ])
    } catch (err) {
      notifyError('That didn’t work', err)
    } finally {
      setBusy(false)
    }
  }
  return (
    <li className="flex gap-3 px-3 py-3">
      {p.image_url && <img src={p.image_url} alt="" className="size-14 shrink-0 rounded-[24px] object-cover" />}
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-bold', CHIP[STATUS_TONE[p.status]])}>
            {p.status === 'posting' && <Loader2 className="mr-1 inline size-3 animate-spin" />}
            {p.status_label}
          </span>
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {p.status === 'scheduled' && p.scheduled_for
              ? `Goes out ${new Date(p.scheduled_for).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
              : timeAgo(p.posted_at ?? p.created_at)}
          </span>
        </div>
        <p className="line-clamp-2 text-sm text-neutral-800 dark:text-neutral-200">{p.title ? `${p.title} — ` : ''}{p.text}</p>
        <div className="flex flex-wrap gap-1.5">
          {p.results.map((r) =>
            r.ok ? (
              r.url ? (
                <a key={r.network} href={r.url} target="_blank" rel="noreferrer" className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold', CHIP.emerald)}>
                  <Check className="size-3" /> {r.label} <ExternalLink className="size-3" />
                </a>
              ) : (
                <span key={r.network} className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold', CHIP.emerald)}>
                  <Check className="size-3" /> {r.label}
                </span>
              )
            ) : r.error ? (
              <span key={r.network} title={r.error} className={cn('inline-flex max-w-full items-center gap-1 truncate rounded-full px-2.5 py-0.5 text-xs font-semibold', CHIP.red)}>
                <X className="size-3 shrink-0" /> {r.label}: {r.error}
              </span>
            ) : (
              <span key={r.network} className={cn('rounded-full px-2.5 py-0.5 text-xs font-semibold', CHIP.neutral)}>
                {r.label}
              </span>
            )
          )}
          {p.results
            .filter((r) => r.image)
            .map((r) => (
              <a
                key={`${r.network}-image`}
                href={r.image}
                target="_blank"
                rel="noreferrer"
                className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold', CHIP.neutral)}
              >
                <ImagePlus className="size-3" /> {r.label} image <ExternalLink className="size-3" />
              </a>
            ))}
        </div>
      </div>
      <div className="flex shrink-0 flex-col gap-1.5">
        {(p.status === 'failed' || p.status === 'partly') && (
          <button type="button" disabled={busy} onClick={() => run(() => retrySocialPost(p.id), 'Trying again…')} className="rounded-full border border-neutral-200 px-3 py-1 text-xs font-semibold text-neutral-900 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-800 dark:text-white dark:hover:bg-neutral-900">
            Retry
          </button>
        )}
        {(p.status === 'failed' || p.status === 'partly') && (
          <button type="button" disabled={busy} onClick={() => run(() => cancelSocialPost(p.id), 'Removed')} className="rounded-full border border-neutral-200 px-3 py-1 text-xs font-semibold text-neutral-900 hover:border-red-300 hover:text-red-600 disabled:opacity-50 dark:border-neutral-800 dark:text-white dark:hover:text-red-400">
            Remove
          </button>
        )}
        {p.status === 'scheduled' && (
          <button type="button" disabled={busy} onClick={() => run(() => cancelSocialPost(p.id), 'Cancelled')} className="rounded-full border border-neutral-200 px-3 py-1 text-xs font-semibold text-neutral-900 hover:border-red-300 hover:text-red-600 disabled:opacity-50 dark:border-neutral-800 dark:text-white dark:hover:text-red-400">
            Cancel
          </button>
        )}
      </div>
    </li>
  )
}
